import { useMemo, useState, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Installation } from '@shared/schema';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { ChevronDown, ChevronUp, ChevronRight, GripVertical } from 'lucide-react';
import { DocumentChecklist } from './document-checklist';
import { SystemDetails } from './system-details';
import { SchedulingIndicator } from './scheduling-indicator';

const STATUSES = [
  'loa_mpan',
  'g98',
  'g99',
  'waiting_site_survey',
  'site_survey',
  'ready_to_schedule',
  'planning_for_installation',
  'prepared_to_install',
  'completed',
] as const;

const statusLabel: Record<string, string> = {
  loa_mpan: 'LoA & MPAN',
  g98: 'G98',
  g99: 'Waiting DNO (G99)',
  waiting_site_survey: 'Waiting for Site Survey',
  site_survey: 'Site Survey Done',
  ready_to_schedule: 'Ready to Schedule',
  planning_for_installation: 'Planning',
  prepared_to_install: 'Prepared',
  completed: 'Completed',
};

const statusColors: Record<string, { border: string; bg: string; headerStyle: React.CSSProperties }> = {
  loa_mpan: { border: 'border-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/20', headerStyle: { backgroundColor: '#2563eb' } },
  g98: { border: 'border-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/20', headerStyle: { backgroundColor: '#4f46e5' } },
  g99: { border: 'border-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/20', headerStyle: { backgroundColor: '#ea580c' } },
  waiting_site_survey: { border: 'border-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/20', headerStyle: { backgroundColor: '#d97706' } },
  site_survey: { border: 'border-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/20', headerStyle: { backgroundColor: '#7c3aed' } },
  ready_to_schedule: { border: 'border-pink-400', bg: 'bg-pink-50 dark:bg-pink-950/20', headerStyle: { backgroundColor: '#db2777' } },
  planning_for_installation: { border: 'border-sky-400', bg: 'bg-sky-50 dark:bg-sky-950/20', headerStyle: { backgroundColor: '#0284c7' } },
  prepared_to_install: { border: 'border-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/20', headerStyle: { backgroundColor: '#059669' } },
  completed: { border: 'border-green-400', bg: 'bg-green-50 dark:bg-green-950/20', headerStyle: { backgroundColor: '#16a34a' } },
};

// Define which stages show which card content
const SHOW_SYSTEM_DETAILS_STAGES = new Set(['waiting_site_survey', 'site_survey']);
const SHOW_DOCUMENT_CHECKLIST_STAGES = new Set(['planning_for_installation', 'prepared_to_install', 'completed']);

export default function InstallationKanban({ search }: { search?: string }) {
  const { toast } = useToast();
  const [compactView, setCompactView] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showNeedsSchedulingOnly, setShowNeedsSchedulingOnly] = useState(false);
  
  const { data: installations = [], isLoading } = useQuery<Installation[]>({
    queryKey: ['/api/installations/grid-view', { q: search || '' }],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '1000', ...(search ? { q: search } : {}) });
      const res = await fetch(`/api/installations/grid-view?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load installations');
      const data = await res.json();
      // Defensive check: ensure we always return an array to prevent "n is not iterable" errors
      return Array.isArray(data) ? data : [];
    },
  });

  // Filter installations based on scheduling toggle
  const filteredInstallations = useMemo(() => {
    if (!showNeedsSchedulingOnly) return installations;
    return installations.filter(i => i.schedulingRequired === true);
  }, [installations, showNeedsSchedulingOnly]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = Object.fromEntries(STATUSES.map((s) => [s, []]));
    for (const i of filteredInstallations) {
      let s = (i.status as string) || 'loa_mpan';
      if (s === 'waiting_dno_approval') s = 'g99';
      if (!g[s]) s = 'loa_mpan';
      g[s].push(i);
    }
    return g;
  }, [filteredInstallations]);

  const totalCount = filteredInstallations.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {totalCount} installations across {STATUSES.length} stages
            {showNeedsSchedulingOnly && ` (filtered from ${installations.length})`}
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={showNeedsSchedulingOnly}
              onCheckedChange={setShowNeedsSchedulingOnly}
              id="scheduling-filter"
            />
            <Label
              htmlFor="scheduling-filter"
              className="text-sm font-medium cursor-pointer"
            >
              Show Needs Scheduling Only
            </Label>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCompactView(!compactView)}
          className="text-xs"
          data-testid="toggle-compact-view"
        >
          {compactView ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronUp className="h-3 w-3 mr-1" />}
          {compactView ? 'Expand Cards' : 'Compact View'}
        </Button>
      </div>
      
      <div className="relative flex-1 min-h-0">
        {/* Right edge gradient to hint more content */}
        {!hasScrolled && (
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none flex items-center justify-end pr-2">
            <div className="text-xs text-muted-foreground animate-pulse flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
            </div>
          </div>
        )}
        <div
          className="overflow-x-auto h-full"
          onScroll={(e) => {
            if (e.currentTarget.scrollLeft > 50 && !hasScrolled) {
              setHasScrolled(true);
            }
          }}
        >
          <div className="flex flex-row flex-nowrap gap-1.5 min-h-0" style={{ minWidth: '1800px' }}>
            {STATUSES.map((s) => (
              <KanbanColumn
                key={s}
                id={s}
                title={statusLabel[s]}
                colors={statusColors[s]}
                items={grouped[s]}
                compact={compactView}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const KanbanColumn = memo(function KanbanColumn({
  id,
  title,
  colors,
  items,
  compact,
}: {
  id: string;
  title: string;
  colors: { border: string; bg: string; headerStyle: React.CSSProperties };
  items: any[];
  compact: boolean;
}) {
  const { toast } = useToast();
  const [isDragOver, setIsDragOver] = useState(false);

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const raw = e.dataTransfer.getData('text/plain');
    const installId = Number(raw);
    if (!installId) return;
    try {
      await apiRequest({ method: 'PATCH', url: `/api/installations/${installId}`, data: { status: id } });
      queryClient.invalidateQueries({ queryKey: ['/api/installations/grid-view'] });
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
      toast({ title: 'Status updated', description: `#${installId} → ${statusLabel[id]}` });
    } catch (err: any) {
      toast({ title: 'Update failed', description: err?.message || 'Server error', variant: 'destructive' });
    }
  };

  return (
    <div
      id={id}
      data-droppable
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={onDrop}
      className={`
        flex flex-col rounded-lg border-2 transition-all flex-shrink-0
        ${colors.border} ${colors.bg}
        ${isDragOver ? 'ring-2 ring-primary ring-offset-1 scale-[1.01]' : ''}
      `}
      style={{ height: 'calc(100vh - 220px)', minHeight: '400px', width: '220px' }}
    >
      <div className="sticky top-0 z-10 px-3 py-2 rounded-t-md text-white font-medium text-sm flex items-center justify-between" style={colors.headerStyle}>
        <span className="truncate">{title}</span>
        <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs font-bold ml-1">
          {items.length}
        </Badge>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 scrollbar-thin">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-xs text-muted-foreground italic">
            No installations
          </div>
        ) : (
          items.map((item) => (
            <KanbanCard key={item.id} item={item} compact={compact} />
          ))
        )}
      </div>
    </div>
  );
});

const KanbanCard = memo(function KanbanCard({ item, compact }: { item: any; compact: boolean }) {
  const customerName = item.customer?.name || 'Unknown';
  const address = item.installationAddress || item.customer?.address || '';
  const postcode = item.customer?.postcode || '';
  const status = item.status || 'loa_mpan';

  // Determine what content to show based on status
  const showSystemDetails = SHOW_SYSTEM_DETAILS_STAGES.has(status);
  const showDocumentChecklist = SHOW_DOCUMENT_CHECKLIST_STAGES.has(status);

  // Extract document statuses for the checklist - memoized to prevent unnecessary re-renders
  const documentStatus = useMemo(() => ({
    invoicePaid: item.checklistInvoicePaid === 'completed',
    evcpApplication: item.checklistEvcpApplication === 'completed',
    g99Commissioning: item.checklistG99Commissioning === 'completed',
    mcsCert: item.checklistMcsCertification === 'completed',
    eicCert: item.checklistEicCertificateProvided === 'completed',
    partP: item.checklistPartPBuildingRegsCertificate === 'completed',
    ownersManual: item.checklistOwnerManual === 'completed',
    sld: item.checklistSld === 'completed',
    datasheets: item.checklistDatasheets === 'completed',
    invoices: item.checklistInvoicesProvided === 'completed',
    rams: item.ramsCompleted === 'completed' || item.ramsCompleted === true,
  }), [
    item.checklistInvoicePaid,
    item.checklistEvcpApplication,
    item.checklistG99Commissioning,
    item.checklistMcsCertification,
    item.checklistEicCertificateProvided,
    item.checklistPartPBuildingRegsCertificate,
    item.checklistOwnerManual,
    item.checklistSld,
    item.checklistDatasheets,
    item.checklistInvoicesProvided,
    item.ramsCompleted,
  ]);

  return (
    <div
      id={String(item.id)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(item.id));
        e.dataTransfer.effectAllowed = 'move';
      }}
      className={`
        group rounded border border-slate-200 dark:border-slate-700
        bg-white dark:bg-slate-800
        cursor-grab active:cursor-grabbing
        hover:shadow-md hover:border-primary/50 transition-all
        ${compact ? 'p-1.5' : 'p-2'}
      `}
      data-testid={`kanban-card-${item.id}`}
    >
      <div className="flex items-start gap-1">
        <GripVertical className="h-3 w-3 text-muted-foreground/50 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className={`font-medium text-foreground truncate ${compact ? 'text-xs' : 'text-sm'}`}>
            <span className="text-muted-foreground">#{item.id}</span> {customerName}
          </div>
          {!compact && (
            <>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{address}</div>
              {postcode && <div className="text-xs text-muted-foreground/70">{postcode}</div>}
            </>
          )}
          {compact && postcode && (
            <div className="text-[10px] text-muted-foreground/70 truncate">{postcode}</div>
          )}

          {/* Scheduling Indicator - shown on all cards */}
          <SchedulingIndicator
            installationId={item.id}
            schedulingRequired={item.schedulingRequired ?? true}
            compact={compact}
          />

          {/* System Details - shown for site survey stages */}
          {showSystemDetails && (
            <SystemDetails
              panelsDescription={item.panelsDescription}
              panelsQuantity={item.panelsQuantity}
              inverter1={item.inverter1}
              inverter2={item.inverter2}
              inverter3={item.inverter3}
              batteryDescription={item.batteryDescription}
              batteryQuantity={item.batteryQuantity}
              compact={compact}
            />
          )}

          {/* Document Checklist - shown for planning/prepared/completed stages */}
          {showDocumentChecklist && (
            <DocumentChecklist
              installationId={item.id}
              documents={documentStatus}
              compact={compact}
            />
          )}
        </div>
      </div>
    </div>
  );
});
